// Why the beacon fired, so sessionization can end a span exactly on
// 'hidden'/'unload' instead of inferring it from gaps.
type UsageEventType = 'init' | 'navigation' | 'visible' | 'hidden' | 'heartbeat' | 'unload';

type UsagePayload = {
  type: UsageEventType;
  bk_id: number | null;
  session_id: string;
  tab_id: string;
  client_id: number | null;
  url: string;
  created_at: string;
};

// Everything we can determine at trackEvent() time without touching cookies
// or sessionStorage. Identity fields (session_id / tab_id) are filled in
// later, only when we're actually about to send a beacon — so environments
// with no endpoint configured never get identity fields persisted to the browser.
type UsagePayloadDraft = Omit<UsagePayload, 'session_id' | 'tab_id'>;

const SESSION_COOKIE_NAME = 'bk_usage_session_id';
const TAB_STORAGE_KEY = 'bk_usage_tab_id';
const SESSION_TTL_SECONDS = 8 * 60 * 60;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const IDLE_THRESHOLD_MS = 3 * 60 * 1000;
const MAX_QUEUE_SIZE = 100;

let initialized = false;
let bkId: number | null = null;
let eventQueue: UsagePayloadDraft[] = [];
let lastActivityAt = Date.now();
let sessionEndSent = false;
let lastTrackedUrl: string | null = null;

export function init(): void {
  if (initialized) return;
  initialized = true;

  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  window.addEventListener('popstate', trackNavigation);

  document.addEventListener('visibilitychange', () =>
    trackEvent(document.visibilityState === 'visible' ? 'visible' : 'hidden'),
  );

  // Both pagehide and beforeunload fire on tab close on some platforms. The
  // guard prevents a duplicate record in S3 for the same session-end moment.
  const fireSessionEnd = (): void => {
    if (sessionEndSent) return;
    sessionEndSent = true;
    trackEvent('unload');
  };
  window.addEventListener('pagehide', fireSessionEnd);
  window.addEventListener('beforeunload', fireSessionEnd);

  const markActive = (): void => {
    lastActivityAt = Date.now();
  };
  document.addEventListener('mousemove', markActive, { passive: true });
  document.addEventListener('scroll', markActive, { passive: true });
  document.addEventListener('keydown', markActive, { passive: true });

  setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);

  trackEvent('init');
}

export function setUser(newBkId: number | string): void {
  const numeric = Number(newBkId);
  if (!Number.isFinite(numeric) || numeric <= 0) return;
  if (bkId === numeric) return;
  bkId = numeric;
  tryFlush();
}

function heartbeat(): void {
  if (document.visibilityState !== 'visible') return;
  if (Date.now() - lastActivityAt >= IDLE_THRESHOLD_MS) return;
  trackEvent('heartbeat');
}

// rind routinely writes the same URL to history several times per user action
// (Angular router and React both syncing state), and hash-only changes don't
// change the tracked URL. Those carry no new information, so skip them.
function trackNavigation(): void {
  if (currentUrl() === lastTrackedUrl) return;
  trackEvent('navigation');
}

function currentUrl(): string {
  // Intentionally omits window.location.hash: in rind, fragments are
  // scroll anchors / widget state / a11y focus targets, not meaningful
  // navigation. Including them would inflate cardinality.
  return window.location.pathname + window.location.search;
}

function trackEvent(type: UsageEventType): void {
  // Build the identity-free draft. No cookies or sessionStorage are touched
  // here — that happens in postBeacon() only when we actually send.
  const draft: UsagePayloadDraft = {
    type,
    bk_id: bkId,
    client_id: parseClientId(window.location.pathname),
    url: currentUrl(),
    created_at: new Date().toISOString(),
  };
  lastTrackedUrl = draft.url;

  if (draft.bk_id === null) {
    // We don't know who the bookkeeper is yet. Queue and wait.
    if (eventQueue.length >= MAX_QUEUE_SIZE) {
      eventQueue.shift();
    }
    eventQueue.push(draft);
    return;
  }

  postBeacon(draft);
}

function tryFlush(): void {
  if (bkId === null) return;
  if (eventQueue.length === 0) return;

  const queued = eventQueue;
  eventQueue = [];

  for (const draft of queued) {
    postBeacon({ ...draft, bk_id: bkId });
  }
}

function postBeacon(draft: UsagePayloadDraft): void {
  const endpoint = getEndpoint();
  if (!endpoint) return;

  // Identity fields are generated here (not in trackEvent) so environments
  // where the tracker never sends a beacon never get session_id / tab_id
  // written to their cookies or sessionStorage.
  const payload: UsagePayload = {
    ...draft,
    session_id: readOrCreateSessionId(),
    tab_id: readOrCreateTabId(),
  };

  // sendBeacon serializes the string body as Content-Type: text/plain, which
  // the backend retags to application/json via a dedicated middleware. Returns
  // false if the UA refused to queue (e.g. payload > browser quota). In that
  // case the heartbeat a few minutes later will cover the gap per the brief.
  const queued = navigator.sendBeacon(endpoint, JSON.stringify(payload));
  if (!queued) {
    console.warn('[usage-tracker] sendBeacon refused to queue payload', payload);
  }
}

function getEndpoint(): string | null {
  // Injected at build time by webpack/appConfig/{env}.config.js.
  const bench = (window as unknown as { BENCH?: { usageEvents?: { endpoint?: string } } }).BENCH;
  return bench?.usageEvents?.endpoint ?? null;
}

export function parseClientId(pathname: string): number | null {
  // Call sites pass window.location.pathname which never contains a query
  // string, so the terminator set is just `/` or end-of-string.
  const match = pathname.match(/^\/app\/(\d+)(?:\/|$)/);
  return match ? Number(match[1]) : null;
}

function wrapHistoryMethod(method: 'pushState' | 'replaceState'): void {
  const original = history[method];
  history[method] = function patched(this: History, ...args: Parameters<typeof original>): void {
    // eslint-disable-next-line prefer-spread
    original.apply(this, args);
    trackNavigation();
  };
}

function readOrCreateSessionId(): string {
  const existing = readCookie(SESSION_COOKIE_NAME);
  const sessionId = existing ?? crypto.randomUUID();
  // Re-set on every read so the 8h TTL is rolling rather than fixed from first issue.
  writeSessionCookie(sessionId);
  return sessionId;
}

function readOrCreateTabId(): string {
  const existing = sessionStorage.getItem(TAB_STORAGE_KEY);
  if (existing) return existing;
  const tabId = crypto.randomUUID();
  sessionStorage.setItem(TAB_STORAGE_KEY, tabId);
  return tabId;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

function writeSessionCookie(value: string): void {
  const segments = [
    `${SESSION_COOKIE_NAME}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${SESSION_TTL_SECONDS}`,
  ];

  // SameSite=None requires Secure or the browser rejects the cookie outright.
  // On non-HTTPS origins (localhost, http dev) fall back to SameSite=Lax so
  // the cookie at least persists same-origin for developer testing.
  if (window.location.protocol === 'https:') {
    segments.push('SameSite=None', 'Secure');
  } else {
    segments.push('SameSite=Lax');
  }

  const domain = computeCookieDomain(window.location.hostname);
  if (domain) {
    segments.push(`Domain=${domain}`);
  }

  document.cookie = segments.join('; ');
}

export function computeCookieDomain(hostname: string): string | null {
  // localhost and bare IPs can't carry a Domain attribute at all.
  if (hostname === 'localhost') return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;

  const labels = hostname.split('.');
  // Require at least 3 labels. Stripping the leading label from a 2-label
  // hostname like `bench.co` would leave `.co`, which is a public suffix
  // (bare TLD) and the browser would reject it. All rind hostnames in
  // dev/staging/prod have 3+ labels today; this guard is defense against
  // someone running the tracker on a hostname that's already as short as
  // it can be registered.
  if (labels.length < 3) return null;

  // Strip the leading label so the cookie is shared across sibling subdomains
  // (e.g. dashboard.in.bench.co and bk.in.bench.co both see .in.bench.co).
  return `.${labels.slice(1).join('.')}`;
}
